import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const SEO2025 = ({ name }) => {
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    // Scroll to top on mount or when name changes
    window.scrollTo(0, 0);
  }, [name]);

  useEffect(() => {
    // Initialize WOW.js for animations
    if (typeof window !== 'undefined' && window.WOW) {
      new window.WOW().init();
    }

    // Google Analytics
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      gtag('js', new Date());
      gtag('config', 'G-P2TS4436R5');
    }
  }, []);

  // Scroll progress + back-to-top visibility
  useEffect(() => {
    const handleScrollProgress = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 307.919 : 0;

      setScrollProgress(progress);
      setShowScroll(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScrollProgress);
    handleScrollProgress();

    return () => window.removeEventListener('scroll', handleScrollProgress);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Adjust for fixed navbar height
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth',
      });
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Best Software Development Company in Hyderabad</title>
        <meta
          name="description"
          content="Discover the Best Software Development Company in Hyderabad offering HMS, HRMS, healthcare and learning solutions to empower businesses with innovation."
        />
      </Helmet>

      {/* Navbar */}
      <Navbar />

      {/* Hero Section with Banner Image */}
      <header className="relative w-full">
        <div
          className="relative w-full h-64 sm:h-80 md:h-96 lg:h-[420px] bg-center bg-cover bg-no-repeat"
          style={{ backgroundImage: "url('../img/development.jpg')" }}
        >
          {/* Dark overlay */}
          <div className="absolute inset-0  bg-opacity-60" />

          {/* Text content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-4">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-center">
              Best Software Development Company 
              <br></br>in Hyderabad
            </h1>
            <div className="flex items-center gap-2 text-sm sm:text-base">
              <Link to="/" className="hover:underline">
                Home
              </Link>
              <svg
                width="7"
                height="10"
                viewBox="0 0 7 10"
                fill="none"
                className="mt-1"
              >
                <path
                  d="M0.297 0.262C0.105 0.426 -0.002 0.648 0 0.878C0.002 1.109 0.113 1.329 0.308 1.492L4.02 4.604L0.366 7.711C0.173 7.875 0.067 8.097 0.069 8.328C0.071 8.558 0.182 8.778 0.377 8.941C0.474 9.022 0.59 9.086 0.717 9.13C0.844 9.174 0.981 9.196 1.118 9.195C1.255 9.194 1.391 9.171 1.518 9.126C1.644 9.081 1.758 9.016 1.854 8.933L6.232 5.212C6.329 5.131 6.405 5.034 6.457 4.927C6.509 4.821 6.535 4.706 6.534 4.591C6.533 4.476 6.505 4.362 6.451 4.256C6.397 4.15 6.319 4.054 6.221 3.974L1.774 0.254C1.677 0.173 1.561 0.109 1.434 0.065C1.307 0.021 1.171 -0.001 1.033 0C0.896 0.001 0.76 0.024 0.634 0.069C0.507 0.114 0.393 0.18 0.297 0.262Z"
                  fill="white"
                />
              </svg>
              <span>Best Software Development Company in Hyderabad</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 py-16 lg:py-24 bg-gradient-to-b from-gray-50 to-gray-100 rounded-3xl shadow-md">
        <div className="flex flex-col lg:flex-row lg:gap-12">
          {/* Main Content */}
          <article className="lg:w-3/4 space-y-12">
            {/* Intro Block */}
            <header className="text-center mb-12">
             
              <h1
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 tracking-tight drop-shadow-md animate-fadeIn"
                data-wow-delay="0.1s"
              >
                Best Software Development Company in Hyderabad
              </h1>
              <p
                className="text-base sm:text-lg text-gray-600 mt-4 max-w-3xl mx-auto leading-relaxed animate-fadeIn"
                data-wow-delay="0.2s"
              >
                The right technology partner helps businesses build applications, websites and digital platforms that
                improve efficiency, security and customer engagement. Working with the{' '}
                <strong>
                  <em>
                    <a
                      href="/"
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      Best Software Development Company in Hyderabad
                    </a>
                  </em>
                </strong>{' '}
                ensures that software becomes the foundation for growth in today’s competitive market.
              </p>

              {/* Overview Image Placeholder */}
              <div className="image-placeholder mt-8 flex justify-center">
                <img
                  src="/img/blog21.jpg"
                  alt="Best software development company in Hyderabad overview"
                />
              </div>
            </header>

            {/* 2. Need for Custom Software Development */}
            <section
              id="need-custom"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                Need for Custom Software Development
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                From managing operations to reaching new customers, businesses depend on technology every day. Partnering
                with the right development company ensures:
              </p>
              <ul className="list-disc pl-6 text-base sm:text-lg text-gray-700 space-y-2">
                <li>Secure and reliable IT systems</li>
                <li>Customized solutions aligned with business goals</li>
                <li>Cost savings through automation and optimization</li>
                <li>Smooth digital experiences for customers and teams</li>
              </ul>

              {/* Image Placeholder */}
              <div className="image-placeholder my-5 flex justify-center">
                <img
                  src="/img/blog22.jpg"
                  alt="Need for custom software development"
                />
              </div>
            </section>

            {/* 3. Benefits of Choosing the Right Enterprise Software */}
            <section
              id="benefits"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                Benefits of Choosing the Right Enterprise Software
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                A trusted{' '}
                <strong>
                  <em>
                    <a
                      href="https://www.securxperts.com/custom-software-development"
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      software development partner
                    </a>
                  </em>
                </strong>{' '}
                provides more than just code. The right enterprise software brings:
              </p>
              <ul className="list-disc pl-6 text-base sm:text-lg text-gray-700 space-y-2">
                <li>Skilled experts with deep industry knowledge</li>
                <li>Scalable solutions that grow with your business</li>
                <li>Latest technologies like AI, Cloud and Analytics</li>
                <li>Strong cybersecurity practices to protect data and systems</li>
              </ul>

              {/* Image Placeholder */}
              <div className="image-placeholder my-5 flex justify-center">
                <img
                  src="/img/blog23.jpg"
                  alt="Benefits of the right enterprise software"
                />
              </div>
            </section>

            {/* 4. IT Challenges Today */}
            <section
              id="it-challenges"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">IT Challenges Today</h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                Companies often face issues such as cyber threats, slow adoption of new tools, poor integration between
                systems and rising IT costs. Without the right support, these challenges can slow down growth, create
                inefficiencies and impact customer trust.
              </p>

              {/* Image Placeholder */}
              <div className="image-placeholder my-5 flex justify-center">
                <img
                  src="/img/blog24.jpg"
                  alt="Modern IT challenges for businesses"
                />
              </div>
            </section>

            {/* 5. SecurXpert – Delivering Top Solutions for Businesses */}
            <section
              id="securxpert-solutions"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                SecurXpert – Delivering Top Solutions for Businesses
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                As the{' '}
                <strong>
                  <em>
                    <a
                      href="https://www.securxperts.com/custom-software-development"
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      Best Software Development Company in Hyderabad
                    </a>
                  </em>
                </strong>
                , SecurXpert delivers secure, scalable and innovative solutions that support real business outcomes:
              </p>
              <ul className="list-disc pl-6 text-base sm:text-lg text-gray-700 space-y-2">
                <li>
                  <strong>Custom Software Development:</strong> Personalized applications to meet unique business needs.
                </li>
                <li>
                  <strong>Digital Marketing:</strong> Strategies that boost online visibility and brand awareness.
                </li>
                <li>
                  <strong>UI/UX Design:</strong> User-friendly websites and apps that improve customer experience.
                </li>
                <li>
                  <strong>IT Consulting:</strong> Guidance for smarter, future-ready technology adoption.
                </li>
                <li>
                  <strong>Cloud Solutions:</strong> Affordable, secure and scalable cloud migration and management.
                </li>
                <li>
                  <strong>BPO Services:</strong> Efficient handling of business processes to save time and cost.
                </li>
                <li>
                  <strong>AI & Analytics:</strong> Data-driven insights that support better decisions.
                </li>
              </ul>

              {/* Image Placeholder */}
              <div className="image-placeholder my-5 flex justify-center">
                <img
                  src="/img/blog25.jpg"
                  alt="SecurXpert software and digital solutions"
                />
              </div>
            </section>

            {/* 6. Our SecurXpert Products */}
            <section
              id="securxpert-products"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">Our SecurXpert Products</h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                Along with services, SecurXpert also provides ready-to-use, industry-focused products that help
                businesses get started quickly:
              </p>
              <ul className="list-disc pl-6 text-base sm:text-lg text-gray-700 space-y-2">
                <li>
                  <strong>One Step Medi:</strong> Doctor onboarding and healthcare marketing platform.
                </li>
                <li>
                  <strong>HRMS:</strong> Employee records, payroll and attendance management solution.
                </li>
                <li>
                  <strong>LauraTek:</strong> Online learning and progress tracking platform.
                </li>
                <li>
                  <strong>HMS:</strong> Hospital Management Software for patient care and operations.
                </li>
                <li>
                  <strong>SAMS:</strong> School Administration Management System for admissions, fees, attendance and
                  academics.
                </li>
              </ul>

              {/* Image Placeholder */}
              <div className="image-placeholder my-5 flex justify-center">
                <img
                  src="/img/blog26.jpg"
                  alt="SecurXpert software products like HMS, HRMS and learning platforms"
                />
              </div>
            </section>

            {/* 7. Driving Business Growth */}
            <section
              id="growth"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">Driving Business Growth</h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                SecurXpert combines technology with strategy to help businesses work smarter, save costs and deliver
                better customer experiences. Our solutions are designed to support long-term growth, digital innovation
                and continuous improvement, making technology a powerful driver of success rather than a challenge.
              </p>

              {/* Image Placeholder */}
              <div className="image-placeholder my-5 flex justify-center">
                <img
                  src="/img/blog27.jpg"
                  alt="Business growth through software and digital solutions"
                />
              </div>
            </section>

            {/* 8. Conclusion */}
            <section
              id="conclusion"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">Conclusion</h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3">
                In today's digital era, success depends on choosing the right technology partner. With secure,
                innovative and scalable solutions, SecurXpert stands out as the{' '}
                <strong>
                  <em>
                    <a
                      href="https://www.securxperts.com/custom-software-development"
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      Best Software Development Company in Hyderabad
                    </a>
                  </em>
                </strong>
                .
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                Whether you need custom applications, ready-to-use products or complete digital transformation support,
                SecurXpert helps you build a strong, future-ready technology foundation for your business.
              </p>
            </section>
          </article>

          {/* Sidebar */}
          <aside className="lg:w-1/4 mt-12 lg:mt-0">
            <div className="sticky top-24 bg-white rounded-2xl shadow-lg p-8 bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
              <h3 className="text-xl font-semibold text-gray-800 mb-6">Quick Navigation</h3>
              <ul className="space-y-4">
                {[
                  { id: 'need-custom', label: 'Need for Custom Software' },
                  { id: 'benefits', label: 'Benefits of Enterprise Software' },
                  { id: 'it-challenges', label: 'IT Challenges Today' },
                  { id: 'securxpert-solutions', label: 'SecurXpert Solutions' },
                  { id: 'securxpert-products', label: 'Our Products' },
                  { id: 'growth', label: 'Driving Business Growth' },
                  { id: 'conclusion', label: 'Conclusion' },
                ].map((item, index) => (
                  <li key={item.id} className="animate-slideInRight" data-wow-delay={`${0.1 * index}s`}>
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className="text-blue-600 hover:text-blue-800 transition-colors text-base font-medium hover:bg-blue-100 rounded-md px-3 py-2 w-full text-left hover:shadow-md"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Scroll to Top with Progress Ring */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg hover:from-blue-600 hover:to-blue-800 transition-all"
        >
          <svg
            className="progress-circle -rotate-90 w-12 h-12 absolute"
            viewBox="-1 -1 102 102"
          >
            <path
              d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray="307.919"
              strokeDashoffset={307.919 - scrollProgress}
              style={{ transition: 'stroke-dashoffset 0.1s linear' }}
            />
          </svg>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6 text-white relative z-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}

      <style jsx>{`
        @keyframes slideInDown {
          0% {
            opacity: 0;
            transform: translateY(-20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeIn {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideInRight {
          0% {
            opacity: 0;
            transform: translateX(20px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        .animate-gradientShift {
          background-size: 200% 200%;
          animation: gradientShift 8s ease infinite;
        }
        a,
        .link {
          text-decoration: none;
        }
        .image-placeholder {
          margin: 20px 0;
          display: flex;
          justify-content: center;
        }
        .image-placeholder img {
          max-width: 500px;
          height: auto;
          object-fit: cover;
          border-radius: 10px;
        }
        @media (max-width: 768px) {
          .image-placeholder img {
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default SEO2025;
